import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from './Icons';
import LinkSecao from './LinkSecao';
import { SLUG_BRASILIA } from '../seo';
import { site, praca, linkWhatsApp } from '../config';
import './Cabecalho.css';

const SITE_UF = praca;

// `secao` rola até um trecho da home; `rota` navega para outra página.
const links = [
  { id: 'inicio', tipo: 'secao', rotulo: 'Início' },
  { id: 'sobre-pagina', tipo: 'rota', para: '/sobre', rotulo: 'O Advogado' },
  { id: 'areas', tipo: 'secao', rotulo: 'Áreas de Atuação' },
  {
    id: 'brasilia',
    tipo: 'rota',
    para: `/${SLUG_BRASILIA}`,
    rotulo: `${SITE_UF}`,
  },
  { id: 'duvidas', tipo: 'secao', rotulo: 'Dúvidas' },
  { id: 'contato-pagina', tipo: 'rota', para: '/contato', rotulo: 'Contato' },
];

const secoes = links.filter((link) => link.tipo === 'secao');

export default function Cabecalho() {
  const { pathname } = useLocation();
  const naHome = pathname === '/';
  const [rolou, setRolou] = useState(false);
  const [oculto, setOculto] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [menuAberto, setMenuAberto] = useState(false);
  const [ativo, setAtivo] = useState('inicio');

  const ultimoY = useRef(0);
  const pausarAte = useRef(0); // trava o auto-ocultar logo após clicar no menu
  const menuAbertoRef = useRef(false);
  const botaoMenuRef = useRef(null);
  const menuRef = useRef(null);

  menuAbertoRef.current = menuAberto;

  /* Fundo sólido, auto-ocultar e barra de progresso — tudo em um só listener. */
  useEffect(() => {
    let agendado = false;

    const atualizar = () => {
      agendado = false;
      const y = window.scrollY;
      const rolavel = document.documentElement.scrollHeight - window.innerHeight;

      setProgresso(rolavel > 0 ? Math.min(y / rolavel, 1) : 0);
      setRolou(y > 40);

      const delta = y - ultimoY.current;
      if (!menuAbertoRef.current && Date.now() > pausarAte.current) {
        if (y > 500 && delta > 6) setOculto(true);
        else if (delta < -6 || y < 260) setOculto(false);
      }
      ultimoY.current = y;
    };

    const aoRolar = () => {
      if (agendado) return;
      agendado = true;
      window.requestAnimationFrame(atualizar);
    };

    atualizar();
    window.addEventListener('scroll', aoRolar, { passive: true });
    window.addEventListener('resize', aoRolar);
    return () => {
      window.removeEventListener('scroll', aoRolar);
      window.removeEventListener('resize', aoRolar);
    };
  }, []);

  /* Destaca no menu a seção que está sendo lida (só existe na home). */
  useEffect(() => {
    if (!naHome) {
      setAtivo(null);
      return undefined;
    }

    setAtivo('inicio');
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const observador = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((entrada) => {
          if (entrada.isIntersecting) setAtivo(entrada.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );

    secoes.forEach(({ id }) => {
      const secao = document.getElementById(id);
      if (secao) observador.observe(secao);
    });

    return () => observador.disconnect();
  }, [naHome]);

  /* Trava a rolagem do fundo com o menu mobile aberto. */
  useEffect(() => {
    document.body.style.overflow = menuAberto ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuAberto]);

  /* Esc fecha o menu e o foco fica preso dentro dele enquanto está aberto. */
  useEffect(() => {
    if (!menuAberto) return undefined;

    const menu = menuRef.current;
    const focaveis = menu ? menu.querySelectorAll('a[href], button') : [];
    if (focaveis.length) focaveis[0].focus({ preventScroll: true });

    const aoTeclar = (evento) => {
      if (evento.key === 'Escape') {
        setMenuAberto(false);
        botaoMenuRef.current?.focus();
        return;
      }

      if (evento.key !== 'Tab' || focaveis.length === 0) return;

      const primeiro = focaveis[0];
      const ultimo = focaveis[focaveis.length - 1];

      if (evento.shiftKey && document.activeElement === primeiro) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault();
        primeiro.focus();
      }
    };

    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [menuAberto]);

  const aoClicarNoLink = useCallback(() => {
    pausarAte.current = Date.now() + 1200;
    setOculto(false);
    setMenuAberto(false);
  }, []);

  const classes = [
    'cabecalho',
    rolou && 'cabecalho--compacto',
    oculto && !menuAberto && 'cabecalho--oculto',
    menuAberto && 'cabecalho--menu-aberto',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <header className={classes}>
      {/* barra de contato */}
      <div className="barra-topo">
        <div className="container barra-topo__interno">
          <span className="barra-topo__plantao">
            <span className="barra-topo__pulso" aria-hidden="true" />
            Plantão 24 horas para casos urgentes
          </span>

          <div className="barra-topo__contatos">
            <a href={`tel:${site.telefoneLink}`}>
              <Icon name="telefone" size={14} />
              {site.telefone}
            </a>
            <a href={`mailto:${site.email}`}>
              <Icon name="email" size={14} />
              {site.email}
            </a>
          </div>
        </div>
      </div>

      {/* barra principal */}
      <div className="container cabecalho__interno">
        <LinkSecao secao="inicio" className="marca" onClick={aoClicarNoLink}>
          <span className="marca__texto">
            <strong>{site.nome}</strong>
            <small>
              {site.cargo} · {site.oab}
            </small>
          </span>
        </LinkSecao>

        <nav className="navegacao" aria-label="Menu principal">
          <ul className="navegacao__lista">
            {links.map((link) => {
              const atual =
                link.tipo === 'rota' ? pathname === link.para : ativo === link.id;
              const classe = `navegacao__link ${atual ? 'ativo' : ''}`;

              return (
                <li key={link.id}>
                  {link.tipo === 'rota' ? (
                    <Link
                      to={link.para}
                      className={classe}
                      aria-current={atual ? 'page' : undefined}
                      onClick={aoClicarNoLink}
                    >
                      {link.rotulo}
                    </Link>
                  ) : (
                    <LinkSecao
                      secao={link.id}
                      className={classe}
                      aria-current={atual ? 'true' : undefined}
                      onClick={aoClicarNoLink}
                    >
                      {link.rotulo}
                    </LinkSecao>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <a
          className="btn btn--ouro cabecalho__cta"
          href={linkWhatsApp()}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon name="whatsapp" size={17} />
          Falar agora
        </a>

        <button
          type="button"
          ref={botaoMenuRef}
          className={`hamburguer ${menuAberto ? 'hamburguer--ativo' : ''}`}
          onClick={() => setMenuAberto((aberto) => !aberto)}
          aria-expanded={menuAberto}
          aria-controls="menu-mobile"
          aria-label={menuAberto ? 'Fechar menu' : 'Abrir menu'}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>
      </div>

      <span
        className="cabecalho__progresso"
        style={{ transform: `scaleX(${progresso})` }}
        aria-hidden="true"
      />
      </header>

      {/* Menu mobile fora do <header>: ele é fixo em tela cheia e o header
          recebe transform ao se esconder, o que quebraria o posicionamento. */}
      <div
        className={`menu-fundo ${menuAberto ? 'menu-fundo--visivel' : ''}`}
        onClick={() => setMenuAberto(false)}
        aria-hidden="true"
      />

      <div
        id="menu-mobile"
        ref={menuRef}
        className={`menu-mobile ${menuAberto ? 'menu-mobile--aberto' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu de navegação"
      >
        <ul className="menu-mobile__lista">
          {links.map((link, indice) => (
            <li key={link.id} style={{ transitionDelay: `${80 + indice * 45}ms` }}>
              {link.tipo === 'rota' ? (
                <Link
                  to={link.para}
                  onClick={aoClicarNoLink}
                  aria-current={pathname === link.para ? 'page' : undefined}
                  className={pathname === link.para ? 'ativo' : undefined}
                >
                  <span className="menu-mobile__numero" aria-hidden="true">
                    {String(indice + 1).padStart(2, '0')}
                  </span>
                  {link.rotulo}
                </Link>
              ) : (
                <LinkSecao
                  secao={link.id}
                  onClick={aoClicarNoLink}
                  aria-current={ativo === link.id ? 'true' : undefined}
                  className={ativo === link.id ? 'ativo' : undefined}
                >
                  <span className="menu-mobile__numero" aria-hidden="true">
                    {String(indice + 1).padStart(2, '0')}
                  </span>
                  {link.rotulo}
                </LinkSecao>
              )}
            </li>
          ))}
        </ul>

        <div className="menu-mobile__rodape">
          <a
            className="btn btn--ouro btn--bloco"
            href={linkWhatsApp()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMenuAberto(false)}
          >
            <Icon name="whatsapp" size={17} />
            WhatsApp
          </a>
          <a className="btn btn--contorno btn--bloco" href={`tel:${site.telefoneLink}`}>
            <Icon name="telefone" size={17} />
            {site.telefone}
          </a>
          <p className="menu-mobile__oab">{site.oab}</p>
        </div>
      </div>
    </>
  );
}
